import { useState, type ChangeEvent, type KeyboardEvent } from 'react';

// Keys that would start a signed or exponent number — kroner amounts are plain unsigned figures,
// so they are blocked before they ever reach the input. Decimal separators (both '.' and the
// Danish ',') are additionally blocked for whole-kroner fields.
const BLOCKED_KEYS = new Set(['-', '+', 'e', 'E']);
const DECIMAL_KEYS = new Set(['.', ',']);

export interface KronerInput {
  text: string;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export interface KronerInputOptions {
  /** Decimal places accepted; 0 also blocks the decimal separator keys. */
  maxDecimals?: 0 | 2;
  /** Whether 0 commits as an amount of its own, distinct from an amount that is not set. */
  allowZero?: boolean;
}

/**
 * Shared behavior for the plain number inputs that edit an optional kroner amount (a plan's
 * completion amount, a group's reimbursement). An amount is committed only when it has at most
 * `maxDecimals` decimal places and is above 0, or exactly 0 when `allowZero` is set; clearing
 * the field commits undefined. The input mirrors what is typed while editing but snaps back to
 * the committed value on blur, so text that is never committed (a pasted "1500.755", or "0"
 * where zero is not allowed) can never linger as a display/save mismatch.
 */
export function useKronerInput(
  value: number | undefined,
  onCommit: (value: number | undefined) => void,
  { maxDecimals = 0, allowZero = false }: KronerInputOptions = {}
): KronerInput {
  const [text, setText] = useState(value?.toString() ?? '');

  // Re-derive the text when the committed value changes underneath us (e.g. another template is
  // loaded) — the adjust-state-during-render pattern from the React docs.
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setText(value?.toString() ?? '');
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value;
    setText(raw);
    if (raw === '') {
      onCommit(undefined);
      return;
    }
    // A number input always reports its value in dot-decimal format, whatever the user typed.
    const decimals = raw.split('.')[1]?.length ?? 0;
    const parsed = Number(raw);
    const withinRange = allowZero ? parsed >= 0 : parsed > 0;
    if (Number.isFinite(parsed) && withinRange && decimals <= maxDecimals) {
      onCommit(parsed);
    }
  };

  const handleBlur = (): void => {
    setText(value?.toString() ?? '');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (BLOCKED_KEYS.has(event.key) || (maxDecimals === 0 && DECIMAL_KEYS.has(event.key))) {
      event.preventDefault();
    }
  };

  return { text, handleChange, handleBlur, handleKeyDown };
}
